/**
 * @description Для использования необходимо оборачивать в какой-нибудь фрагмент по типу span
 * и нужно передать в поле menu строку либо свою структуру
 * Тултип имеет две цветовые схемы темную и светлую
 * также, принимает position, определяет с какой стороны выводить тултип
 * и задержку с которой будет выводить тултип
 *
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */

import React, { cloneElement, PropsWithChildren, ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import sass from "./index.module.sass";
import cn from "classnames";

type ColorScheme = "themeDark" | "themeWhite";
type Position = "top" | "left" | "right" | "bottom" | "top-right" | "top-left";

interface IWrapper extends PropsWithChildren {
	children: ReactElement;
	show?: boolean;
	delay?: number;
	color?: ColorScheme;
	menu: string | ReactElement | ReactNode;
	position?: Position;
}

interface ITooltip extends PropsWithChildren {
	target?: any;
	color?: ColorScheme;
	position?: Position;
	clear: boolean;
	interval: () => void;
	out: () => void;
}

const PortalWindow: React.FC<ITooltip> = ({
	clear,
	target,
	color = "themeDark",
	children,
	out,
	interval,
	position = "top",
}) => {
	const [posWrapper, setPosWrapper] = useState<any | null>(null);
	const [posArrow, setPosArrow] = useState<any | null>(null);

	const ref = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const t: any = target as HTMLElement;
		if (t) {
			const { top, left, width, height, right } = t.getBoundingClientRect();
			let chordWrapper, chordArrow;
			switch (position) {
				case "top":
					chordWrapper = {
						left: left + width / 2,
						top: top - 10,
						transform: "translate(-50%,-100%)",
					};
					chordArrow = {
						bottom: 0.3,
						left: "50%",
						transform: "translate(-50%,50%) rotate(-45deg)",
					};
					setPosWrapper(chordWrapper);
					setPosArrow(chordArrow);
					break;
				case "top-left":
					chordWrapper = {
						left: left + width / 2,
						top: top - 10,
						transform: "translate(-10px,-100%)",
					};
					chordArrow = {
						bottom: 0.3,
						left: 5,
						transform: "translate(0,50%) rotate(-45deg)",
					};
					setPosWrapper(chordWrapper);
					setPosArrow(chordArrow);
					break;
				case "top-right":
					chordWrapper = {
						left: left + width / 2,
						top: top - 10,
						transform: "translate(calc(-100% + 10px),-100%)",
					};
					chordArrow = {
						bottom: 0.3,
						right: 5,
						transform: "translate(0,50%) rotate(-45deg)",
					};
					setPosWrapper(chordWrapper);
					setPosArrow(chordArrow);
					break;
				case "bottom":
					chordWrapper = {
						left: left + width / 2,
						top: top + height + 10,
						transform: "translate(-50%,0%)",
					};
					chordArrow = {
						top: 0.3,
						left: "50%",
						transform: "translate(-50%,-30%) rotate(135deg)",
					};
					let docBot = document.body.getBoundingClientRect().bottom;
					if (ref.current?.getBoundingClientRect().height + chordWrapper.top > docBot) {
						chordWrapper.top = docBot - (ref.current?.getBoundingClientRect().height || 0) - 15;
						chordArrow.top = 0;
						chordArrow.left = "0";
					}
					setPosWrapper(chordWrapper);
					setPosArrow(chordArrow);
					break;
				case "left":
					chordWrapper = {
						left: left - 10,
						top: top + height / 2,
						transform: "translate(-100%,-50%)",
					};
					chordArrow = {
						right: 0.3,
						top: "50%",
						transform: "translate(50%,-30%) rotate(-135deg)",
					};
					setPosWrapper(chordWrapper);
					setPosArrow(chordArrow);
					break;
				case "right":
					chordWrapper = {
						left: right + 10,
						top: top + height / 2,
						transform: "translate(0,-50%)",
					};
					chordArrow = {
						left: 0.3,
						top: "50%",
						transform: "translate(-50%,-50%) rotate(45deg)",
					};
					setPosWrapper(chordWrapper);
					setPosArrow(chordArrow);
					break;
			}
		}
		return () => setPosWrapper(null);
		//eslint-disable-next-line
	}, [target]);

	if (target) {
		return createPortal(
			<div
				ref={ref}
				onMouseOver={interval}
				onMouseLeave={out}
				className={cn(sass.bg, sass[`bg_${color}`], clear ? sass.bg_clear : sass.bg_show)}
				style={{
					...posWrapper,
				}}>
				<div
					className={cn(sass.arrow, sass[`bg_${color}`])}
					style={{
						...posArrow,
					}}
				/>
				{children}
			</div>,
			document.body,
		);
	} else return null;
};

/**
 *
 * @param target - элемент к которому привязывается тултип, использует его положение на странице для отрисовки
 * @param showTooltip - показывать тултип или нет
 * @param color - цветовая схема для тултипа, темная либо светлая
 * @param menu - само меню, то что должно находиться в тултипе
 * @param children - по наведению на children, появится menu
 * @param position - позиция, с какой стороны нужно
 * @constructor
 */
const Tooltip: React.FC<IWrapper> = ({
	children,
	show: showTooltip = true,
	delay = 500,
	position,
	color = "themeDark",
	menu,
}) => {
	const [clear, setClear] = useState(false);

	const [element, setElement] = useState<any>();

	const [show, setShow] = useState(false);

	const [clone, setClone] = useState<ReactNode>();

	const timeRef = useRef<NodeJS.Timeout | null>(null);
	const timeOffRef = useRef<NodeJS.Timeout | null>(null);

	const onMouseEnter = (e: React.MouseEvent<EventTarget>) => {
		const target = e.currentTarget;
		timeRef.current && clearTimeout(timeRef.current);
		timeRef.current = null;
		timeRef.current = setTimeout(() => {
			setElement(target);
			setShow(true);
			setTimeout(() => {
				setClear(false);
			}, 50);
		}, delay);
	};
	const onMouseOut = () => {
		timeRef.current && clearTimeout(timeRef.current);
		timeRef.current = null;
		clearTimer();
		timeOffRef.current = setTimeout(() => {
			setClear(true);
			setTimeout(() => {
				setElement(undefined);
				setShow(false);
			}, 200);
		}, 50);
	};
	const clearTimer = () => {
		if (timeOffRef.current) clearTimeout(timeOffRef.current);
		timeOffRef.current = null;
	};

	useEffect(() => {
		if (children) {
			setClone(cloneElement(children, { ...children.props, onMouseOver: onMouseEnter, onMouseLeave: onMouseOut }));
		}
		//eslint-disable-next-line
	}, [children]);

	return (
		<>
			{showTooltip && show && (
				<PortalWindow
					interval={clearTimer}
					out={onMouseOut}
					clear={clear}
					target={element}
					color={color}
					position={position}>
					{menu}
				</PortalWindow>
			)}
			{clone}
		</>
	);
};

export default Tooltip;
